import { useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '../../auth/useAuth.ts';
import { useOfflineQueue } from '../../offline/useOfflineQueue.ts';
import { ConnectionIndicator } from '../../realtime/ConnectionIndicator.tsx';
import { useListRealtime } from '../../realtime/useListRealtime.ts';
import { MemberChip } from '../groups/MemberChip.tsx';
import type { Group } from '../groups/groupsApi.ts';
import { AddItemBar } from './AddItemBar.tsx';
import { ItemEditor } from './ItemEditor.tsx';
import { ItemRow } from './ItemRow.tsx';
import { ListSwitcher } from './ListSwitcher.tsx';
import { StoreSwitcher } from './StoreSwitcher.tsx';
import { PhotoViewer } from './PhotoViewer.tsx';
import { compressImage } from './compressImage.ts';
import { normaliseItemName } from './normaliseItemName.ts';
import { resolveAisle } from './aisleFor.ts';
import { sectionise } from './aisleSections.ts';
import { AISLE_LABELS, type Aisle } from './aisleLabels.ts';
import { applyPending } from './applyPending.ts';
import { useAisles, useClearAisle, useSetAisle } from './useAisles.ts';
import { useSelectedStore } from './useSelectedStore.ts';
import {
  imageFor,
  useAttachImage,
  useItemImageBlob,
  useItemImages,
  useObjectUrl,
  useRemoveImage,
} from './useItemImages.ts';
import { describeError } from '../../ui/problemDetails.ts';
import { isRetryable } from '../../offline/isOfflineError.ts';
import { revealItem, revealItemAsKeyboardOpens } from './revealItem.ts';
import { useForgetSuggestion } from './useForgetSuggestion.ts';
import type { Item, ShoppingList } from './itemsApi.ts';
import {
  useAddItem,
  useClearChecked,
  useDeleteItem,
  useItems,
  useSuggestions,
  useToggleItem,
  useUpdateItem,
  uuidv7,
} from './useItems.ts';

/**
 * The screen the app exists for.
 *
 * Outstanding items first, ticked ones collapsed underneath — a shopper looks at what
 * is still to get, and the ticked pile is reference material.
 */
interface ListViewProps {
  group: Group;
  lists: ShoppingList[];
  /** The list showing. Null only while the group's lists are still loading. */
  list: ShoppingList | null;
  onSelectList: (listId: string) => void;
}

export function ListView({ group, lists, list, onSelectList }: ListViewProps) {
  const { user } = useAuth();

  const listId = list?.id;

  // Realtime first, because the items query needs to know whether the hub is carrying
  // this list: when it is not, the query polls instead of trusting a cache nothing is
  // updating. See the note on DisconnectedPollMs.
  const realtime = useListRealtime(listId);

  const { data: serverItems, isPending } = useItems(listId, {
    isLive: realtime.connection === 'live',
  });
  const { data: suggestions } = useSuggestions(listId);

  const offline = useOfflineQueue();

  // What the server will do with an added item, mirrored so the optimistic row is drawn
  // with the right colour rather than acquiring one a round trip later.
  const assignNewTo = list?.assignNewItemsToCreator ? (user?.id ?? null) : null;

  // What the server last said, plus everything queued since. Within a session the query
  // cache already carries the optimistic changes and this replays nothing; after a reload
  // it is the only thing that knows about them, because the cache is memory and the
  // service worker's copy of this list predates them. See applyPending.
  const items = useMemo(
    () =>
      applyPending(serverItems ?? [], offline.entries, listId ?? '', {
        currentUserId: user?.id ?? null,
        assignNewTo,
      }),
    [serverItems, offline.entries, listId, user?.id, assignNewTo],
  );

  const addItem = useAddItem(listId ?? '', assignNewTo);
  const toggleItem = useToggleItem(listId ?? '', user?.id);
  const clearChecked = useClearChecked(listId ?? '');
  const updateItem = useUpdateItem(listId ?? '');
  const deleteItem = useDeleteItem(listId ?? '');
  const forgetSuggestion = useForgetSuggestion(group.id);

  // Only one row is editable at a time. Several open editors on a phone screen would
  // push the list around under someone's thumb.
  const [editingId, setEditingId] = useState<string | null>(null);

  // Photos are filed under the group's names, so one small catalogue answers for every
  // row rather than each item carrying its own field. See useItemImages.
  const { data: photos } = useItemImages(group.id);
  const attachPhoto = useAttachImage();
  const removePhoto = useRemoveImage();
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [photoQueued, setPhotoQueued] = useState(false);

  // Only the row being edited needs its bytes: the list itself shows an icon, not a
  // thumbnail, so nothing else pays for a photo it is not displaying.
  const editingItem = items.find((item) => item.id === editingId);
  const editingPhoto = editingItem ? imageFor(photos, editingItem.name) : undefined;
  const { data: editingPhotoBlob } = useItemImageBlob(group.id, editingPhoto?.id);
  const editingPhotoUrl = useObjectUrl(editingPhotoBlob);

  // Aisles are filed under the group's names too, so this is the same shape as the photo
  // catalogue and for the same reason. It carries only the household's *corrections* —
  // the ordinary answer comes from the table in the bundle, with no network at all.
  const { corrections, stores, orderFor } = useAisles(group.id);
  const setAisle = useSetAisle(group.id);
  const clearAisle = useClearAisle(group.id);

  // Which shop this phone is in. Local to the device on purpose — see useSelectedStore —
  // so one of you can be walking Tesco and the other Aldi with the same list open.
  const { selected: store, select: selectStore } = useSelectedStore(group.id, stores);
  const aisleOrder = orderFor(store?.id);

  const editingAisle = editingItem ? resolveAisle(editingItem.name, corrections) : undefined;
  const editingAisleCorrected = editingItem
    ? corrections.has(normaliseItemName(editingItem.name))
    : false;

  // The item whose photo is being looked at, if any. The item rather than the image id,
  // because the dialog is titled with the name and the alt text needs it too.
  const [viewingPhotoFor, setViewingPhotoFor] = useState<Item | null>(null);
  const viewingPhoto = viewingPhotoFor ? imageFor(photos, viewingPhotoFor.name) : undefined;

  // The row to shake because you just tried to add something already on the list.
  const [duplicateId, setDuplicateId] = useState<string | null>(null);

  const membersById = useMemo(
    () => new Map(group.members.map((member) => [member.userId, member])),
    [group.members],
  );

  const { outstanding, done } = useMemo(() => split(items), [items]);

  // Only the outstanding items. "In the trolley" stays flat on purpose: it is a receipt
  // rather than a route, ordered most-recently-ticked first because that is the one you
  // might have ticked by mistake, and sectioning it would throw that ordering away to
  // answer a question nobody asks about things they have already got.
  const sections = useMemo(
    () =>
      list?.groupByAisle
        ? sectionise(outstanding, aisleOrder, (name) => resolveAisle(name, corrections))
        : [],
    [outstanding, aisleOrder, corrections, list?.groupByAisle],
  );

  // An added item lands at the *end* of the outstanding list, which on a list of any
  // length is below the fold — and the sticky add bar covers the last of it. Set when we
  // add, consumed once React has drawn the optimistic row: an effect rather than a frame
  // callback because the row is only in the DOM after the commit that follows the
  // mutation, and "after the commit" is exactly what an effect is.
  const revealOnRender = useRef<string | null>(null);

  useEffect(() => {
    const itemId = revealOnRender.current;

    if (itemId === null) {
      return;
    }

    revealOnRender.current = null;
    revealItem(itemId);
  }, [items]);

  if (!listId || !list) {
    return <p className="muted">Setting up your list…</p>;
  }

  return (
    <>
      {/* Directly under the group name, with nothing between them: the household and the
          list are one heading in two sizes, and the block of member chips that used to sit
          in the gap belongs to the rows rather than to the title.

          The shop shares this line rather than taking one of its own. Both switchers hide
          themselves when they have one option, so on most households this row draws
          nothing at all and collapses to no height — which is why it can be rendered
          unconditionally rather than guarded here. */}
      <div className="switcher-row">
        <ListSwitcher lists={lists} selected={list} onSelect={onSelectList} />

        {store && <StoreSwitcher stores={stores} selected={store} onSelect={selectStore} />}
      </div>

      {/* Who is on this list, and whether what it says is real. One line, because they are
          the same question asked twice — the legend names the colours on the rows, and the
          status says whether anyone else is looking at them right now. It is also the line
          that disappears entirely on a one-person list with one list in it. */}
      <div className="list-meta">
        {group.members.length > 1 && (
          <ul className="member-list member-list--header">
            {group.members.map((member) => (
              <li key={member.userId}>
                <MemberChip member={member} />
              </li>
            ))}
          </ul>
        )}

        <ConnectionIndicator
          state={realtime.connection}
          present={realtime.present}
          currentUserId={user?.id}
          queued={offline.queued}
          isFlushing={offline.isFlushing}
        />
      </div>

      <AddItemBar
        suggestions={suggestions ?? []}
        onAdd={handleAdd}
        onForgetSuggestion={(name) => forgetSuggestion.mutate(name)}
        // Tapping the add box brings the end of the list up with it, so the next thing
        // added appears where someone is already looking rather than behind the keyboard.
        onReachForInput={() => {
          const last = outstanding[outstanding.length - 1];

          if (last) {
            revealItemAsKeyboardOpens(last.id);
          }
        }}
      />

      {isPending && <p className="muted">Loading…</p>}

      {!isPending && outstanding.length === 0 && done.length === 0 && (
        <p className="muted empty-state">
          Nothing on the list. Add something above — or tap one of the suggestions once
          you&rsquo;ve shopped a few times.
        </p>
      )}

      {/* Sections when the list asks for them, one flat column when it does not. Both
          draw exactly the same rows in exactly the same order within a section — the
          setting decides where the headings go, not what the list contains. */}
      {list.groupByAisle ? (
        sections.map((section) => (
          <section key={section.aisle} className="aisle-section">
            <h2 className="aisle-section__title">{AISLE_LABELS[section.aisle]}</h2>

            <ul className="item-list">{section.items.map(renderItem)}</ul>
          </section>
        ))
      ) : (
        <ul className="item-list">{outstanding.map(renderItem)}</ul>
      )}

      {done.length > 0 && (
        <section className="done-section">
          <div className="done-section__header">
            <h2 className="done-section__title">In the trolley ({done.length})</h2>
            <button
              type="button"
              className="button button--quiet button--small"
              onClick={() => clearChecked.mutate()}
              disabled={clearChecked.isPending}
            >
              Clear
            </button>
          </div>

          <ul className="item-list">{done.map(renderItem)}</ul>
        </section>
      )}

      {/* Rendered where its data already is, like the other panels. It unmounts on close
          rather than having a closed state, which is what Modal expects. */}
      {viewingPhotoFor && viewingPhoto && (
        <PhotoViewer
          groupId={group.id}
          photo={viewingPhoto}
          name={viewingPhotoFor.name}
          onClose={() => setViewingPhotoFor(null)}
        />
      )}

      {/* Scroll room for the keyboard, and nothing else — see the note on the class. */}
      <div className="keyboard-spacer" aria-hidden="true" />
    </>
  );

  /**
   * One row, or the editor that has replaced it.
   *
   * Written once and used three times — the outstanding column, each aisle section, and
   * the trolley. It was two copies before the sections existed, which was already one too
   * many; three would have meant a prop added to the editor could reach two of them and be
   * missed on the third, which is precisely the bug nobody finds until they open a card in
   * the wrong half of the screen.
   *
   * `checkedBy` resolves to undefined for anything unticked, so the two halves of the list
   * need no branch between them: an outstanding item has no `checkedByUserId` to look up.
   */
  function renderItem(item: Item) {
    if (item.id === editingId) {
      return (
        <ItemEditor
          key={item.id}
          item={item}
          members={group.members}
          checkedBy={item.checkedByUserId ? membersById.get(item.checkedByUserId) : undefined}
          onSave={(changes) => saveEdit(item, changes)}
          onDelete={() => removeItem(item)}
          onToggle={() => toggleFrom(item)}
          onCancel={() => setEditingId(null)}
          photo={editingPhoto}
          photoUrl={editingPhotoUrl}
          onAttachPhoto={(file) => void attachPhotoTo(item, file)}
          // Non-null because nothing renders a row until the list has loaded — but the
          // narrowing that used to prove it lived in the JSX, and a function declaration
          // does not inherit it.
          onRemovePhoto={() => removePhoto.mutate({ listId: listId!, itemId: item.id })}
          onViewPhoto={() => setViewingPhotoFor(item)}
          photoStatus={{ busy: photoBusy, error: photoError, queued: photoQueued }}
          aisle={editingAisle}
          aisleCorrected={editingAisleCorrected}
          // Withheld entirely on a flat list. The control would still work, but it would
          // move a row into a section that is not being drawn — a change with no visible
          // effect, which is a worse thing to offer than no control at all.
          onChangeAisle={list?.groupByAisle ? (aisle) => changeAisle(item, aisle) : undefined}
          onClearAisle={list?.groupByAisle ? () => resetAisle(item) : undefined}
        />
      );
    }

    return (
      <ItemRow
        key={item.id}
        item={item}
        checkedBy={item.checkedByUserId ? membersById.get(item.checkedByUserId) : undefined}
        assignedTo={item.assignedToUserId ? membersById.get(item.assignedToUserId) : undefined}
        isDuplicate={item.id === duplicateId}
        onToggle={toggleFrom}
        onEdit={(target) => setEditingId(target.id)}
        photoId={imageFor(photos, item.name)?.id}
        onViewPhoto={() => setViewingPhotoFor(item)}
      />
    );
  }

  /**
   * Files this item's name in an aisle, for the household.
   *
   * The name goes with it so the optimistic write has something to key on: the correction
   * belongs to the name, and the row is only how it was reached.
   */
  function changeAisle(item: Item, aisle: Aisle) {
    setAisle.mutate({ listId: listId!, itemId: item.id, name: item.name, aisle });
  }

  function resetAisle(item: Item) {
    clearAisle.mutate({ listId: listId!, itemId: item.id, name: item.name });
  }

  /**
   * Adds an item — unless it is already on the list, in which case it points at the one
   * that is there rather than quietly making a second.
   *
   * Checked counts as being on the list: it is in the trolley, not gone. Only a deleted
   * item frees the name up again.
   */
  function handleAdd(name: string) {
    const normalised = normaliseItemName(name);
    // Searches the *drawn* list, so a queued add is a duplicate too. That is the case
    // that bit: after a reload the row was invisible, so the guard let a second one
    // through with a new id, and both landed when the queue drained.
    const existing = items.find((item) => normaliseItemName(item.name) === normalised);

    if (existing) {
      showDuplicate(existing);
      return;
    }

    const id = uuidv7();

    revealOnRender.current = id;
    addItem.mutate({ id, name, rememberedNote: rememberedNote(name) });
  }

  /**
   * The note the server will bring back with this name, as far as this client can tell.
   *
   * Mirrored from the chips, which carry it for exactly this purpose, so a re-added item
   * reads "4 pints" the instant it appears instead of a round trip later — and offline,
   * where the row would otherwise sit blank until the queue drained. It is a prediction,
   * not a request: nothing is sent, and the server decides from the list's own setting.
   *
   * It searches the whole pool the endpoint sends rather than the eight chips drawn from
   * it, which is why a name typed in full now usually predicts something. A name the pool
   * has never heard of still predicts nothing, which costs a late note rather than a
   * wrong one.
   */
  function rememberedNote(name: string): string | null {
    if (!list?.preserveItemNotes) {
      return null;
    }

    const normalised = normaliseItemName(name);

    return (
      (suggestions ?? []).find(
        (suggestion) => normaliseItemName(suggestion.name) === normalised,
      )?.note ?? null
    );
  }

  /**
   * Shrinks a photo and sends it.
   *
   * The compression is the slow part — a phone photograph is several megabytes and has to
   * be decoded, drawn down and re-encoded before anything leaves the device — so the
   * editor is told it is busy for the whole of it rather than only for the upload.
   *
   * The image id is minted here, like an item id, so that a send which is queued or
   * retried lands on the same row instead of orphaning the first one under a URL the
   * browser has already cached.
   */
  async function attachPhotoTo(item: Item, file: File) {
    setPhotoBusy(true);
    setPhotoError(null);
    setPhotoQueued(false);

    try {
      const { blob } = await compressImage(file);

      await attachPhoto.mutateAsync({
        listId: listId!,
        itemId: item.id,
        imageId: uuidv7(),
        blob,
      });
    } catch (error) {
      // Said out loud rather than swallowed: someone watched this photo being taken, and
      // a quota refusal or an undecodable HEIC both need explaining, not a silent no-op.
      //
      // A photo that could not be sent has already been queued by the mutation, so the
      // honest thing to say is that it is safe rather than that it failed.
      if (isRetryable(error)) {
        setPhotoQueued(true);
      } else {
        setPhotoError(describeError(error));
      }
    } finally {
      setPhotoBusy(false);
    }
  }

  function showDuplicate(item: Item) {
    setDuplicateId(item.id);

    // A ticked item lives in the collapsed section further down, so scrolling is not
    // optional — pointing at something off-screen would look like nothing happened.
    // Forced, because a row can be on screen and still not be where the eye is.
    window.requestAnimationFrame(() => revealItem(item.id, { force: true }));

    // Haptics where they exist. On a phone this is felt before it is seen, which is the
    // point when you are looking at a shelf rather than the screen.
    navigator.vibrate?.([40, 60, 40]);

    // Long enough for the animation to finish, then the row goes back to normal.
    window.setTimeout(() => setDuplicateId((current) => (current === item.id ? null : current)), 1200);
  }

  function saveEdit(
    item: Item,
    changes: { name: string; note: string | null; assignedToUserId: string | null },
  ) {
    // Deliberately does not close the editor. An edit now commits on every blur, so
    // saving and collapsing are two different events — closing here would fold the card up
    // the moment someone moved from the name to the note.
    updateItem.mutate({ itemId: item.id, ...changes });
  }

  /** Ticking, from the row and from the tick on an open editor alike. */
  function toggleFrom(item: Item) {
    toggleItem.mutate({ item });
  }

  function removeItem(item: Item) {
    setEditingId(null);
    deleteItem.mutate(item.id);
  }
}

function split(items: Item[]): { outstanding: Item[]; done: Item[] } {
  const outstanding: Item[] = [];
  const done: Item[] = [];

  for (const item of items) {
    (item.isChecked ? done : outstanding).push(item);
  }

  outstanding.sort((a, b) => a.sortOrder - b.sortOrder);
  // Most recently ticked first: that is the one you might have ticked by mistake.
  done.sort((a, b) => (b.checkedAt ?? '').localeCompare(a.checkedAt ?? ''));

  return { outstanding, done };
}
